"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    ALLOCATION_REPOS,
    ARRET_MINIMAL_APRES_JOURS,
    ARRET_MINIMAL_JOURS,
    AFFILIATION_MOIS,
    IJ_FORFAITAIRE,
    MATERNITE,
    PATERNITE_JOURS,
    REPORT_PRENATAL_MAX_JOURS,
    REVENU_PLANCHER,
    concerne,
    congeSupplementaire,
    droitsMaternite,
    droitsPaternite,
    euros,
    recapitulatif,
    valider,
} from "@/lib/tools/parentalLeave";
import { expliquerCongeNaissance, expliquerCongeSupplementaire } from "@/lib/tools/explanations";
import {
    IndiceExplication,
    MontantExplicable,
    PanneauExplication,
    phraseAvecMontants,
    useExplications,
} from "./Explanation";

/** Une date ISO en toutes lettres, dans la langue lue. */
function dateLisible(iso, lang) {
    return new Date(`${iso}T12:00:00Z`).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });
}
import {
    Avancer,
    Champ,
    Choix,
    Ecran,
    Liste,
    Recommencer,
    Resultat,
    Segments,
    remplir,
    useParcours,
} from "./Journey";

/**
 * Congé maternité et congé de paternité du travailleur indépendant.
 *
 * DEUX PRESTATIONS, DEUX LOGIQUES, et c'est tout l'intérêt de l'outil :
 * l'allocation forfaitaire de repos maternel suit la NAISSANCE, les indemnités
 * journalières suivent l'INTERRUPTION. S'arrêter moins de huit semaines ne
 * donne pas une part des indemnités, il n'en donne aucune, et l'allocation
 * reste pourtant acquise.
 *
 * LE PARCOURS DEMANDE L'AFFILIATION EN PREMIER, parce que c'est le seul refus
 * qui ferme tout : six mois à la date présumée de l'accouchement. Ce ne sont pas
 * les douze mois de l'arrêt maladie, et cette confusion fait renoncer des femmes
 * qui ont droit à l'ensemble.
 *
 * Les chiffres viennent du référentiel arbitré, complété le 8 septembre 2026 sur
  * deux sources officielles concordantes. Un sujet n'y est pas chiffré, et
  * l'outil ne l'invente pas : le fractionnement du congé de paternité d'un
  * indépendant. Le congé supplémentaire de naissance s'y ajoute depuis le
 * 25 septembre 2026, sur Service-Public et ameli.fr.
 */

const L = {
    fr: {
        phases: ["Vos droits", "Votre congé", "Vos revenus"],
        retour: "Retour",
        prive: "Rien ne sort de cet onglet : le calcul se fait dans votre navigateur, et tout s'efface quand vous le fermez.",

        parentTitre: "Qui prend le congé ?",
        parentAide: "Les deux congés n'ont ni la même durée ni les mêmes versements, et un seul comporte une allocation forfaitaire.",
        mere: "La mère",
        mereDetail: "Congé maternité : une allocation et des indemnités journalières",
        second: "Le second parent",
        secondDetail: "Congé de paternité et d'accueil de l'enfant : des indemnités seulement",

        affiliationTitre: "Serez-vous affiliée depuis six mois à la date prévue ?",
        affiliationTitreSecond: "Serez-vous affilié depuis six mois au début du congé ?",
        affiliationAide: "Sans ces six mois, la Sécurité sociale ne verse rien. Ce sont six mois d'activité indépendante, et non les douze mois qu'exige l'arrêt maladie : la confusion est courante et coûte cher.",
        oui: "Oui",
        ouiDetail: "L'activité aura plus de six mois",
        non: "Non",
        nonDetail: "L'activité sera plus récente que cela",

        horsSujetTitre: "Les prestations ne sont pas ouvertes",
        horsSujet: "Les prestations maternité et paternité d'un travailleur indépendant supposent six mois d'affiliation à la date présumée de l'accouchement, ou au début du congé. En dessous, ni allocation ni indemnités journalières. Rien ne vous empêche de vous arrêter, mais la Sécurité sociale ne versera rien : c'est un budget à prévoir autrement, et une raison de plus de ne pas caler une création d'entreprise juste avant une naissance.",

        naissanceTitre: "La naissance",
        naissanceAide: "C'est cette date qui pose tout le calendrier : le début du congé, la période obligatoire, et la fin.",
        champDateAccouchement: "Date prévue de l'accouchement",
        champDateNaissance: "Date de naissance",
        aideDateNaissance: "Ou la date prévue, si elle n'est pas encore arrivée.",
        champSituation: "Votre situation",
        premier: "Un enfant, sans autre à charge ou un seul",
        premierDetail: "16 semaines, 6 avant et 10 après",
        troisieme: "Un enfant, avec déjà deux enfants à charge",
        troisiemeDetail: "26 semaines, 8 avant et 18 après",
        jumeaux: "Des jumeaux",
        jumeauxDetail: "34 semaines, 12 avant et 22 après",
        triples: "Des triplés ou plus",
        triplesDetail: "46 semaines, 24 avant et 22 après",
        champNaissances: "Combien d'enfants naissent ?",
        simple: "Un seul",
        multiple: "Plusieurs",
        naissancesNote: "Une naissance simple ouvre {simple} jours, des naissances multiples {multiple}.",

        arretTitre: "Combien de temps comptez-vous vous arrêter ?",
        arretAideMere: "En dessous de huit semaines d'arrêt, vous ne touchez aucune indemnité journalière, pas même une partie.",
        arretAideSecond: "Le congé se prend dans les six mois qui suivent la naissance, en une fois ou en plusieurs.",
        champJours: "Nombre de jours d'arrêt",
        aideJoursMere: "Le congé complet en ouvre {total}. Le minimum pour toucher quoi que ce soit est de {minimum}, dont {apres} après l'accouchement.",
        aideJoursSecond: "{plafond} jours au plus.",
        toutLeConge: "Prendre le congé complet",
        champReport: "Reporter du prénatal sur le postnatal",
        aideReport: "Jusqu'à {max} jours, sur prescription médicale, à demander au plus tard la veille du début prévu. Le total ne change pas, il se décale.",

        revenuTitre: "Votre revenu des trois dernières années",
        revenuAide: "Sous un certain seuil, l'allocation comme les indemnités tombent à un dixième. Au-dessus, elles sont forfaitaires : elles ne montent pas avec le revenu.",
        champRevenuConnu: "Votre revenu d'activité annuel moyen",
        revenuChiffre: "Je le connais",
        revenuSous: "Il est faible",
        revenuSousDetail: "Moins de {plancher}",
        champRevenuMoyen: "Revenu annuel moyen",
        aideRevenuMoyen: "La moyenne de vos trois dernières années civiles d'activité. En micro-entreprise, c'est le chiffre d'affaires après abattement, pas le chiffre d'affaires brut.",

        continuer: "Continuer",
        voirResultat: "Voir mes droits",
        resultatTitre: "Ce que vous toucherez",
        totalTitre: "Total des prestations",
        recapTitre: "Le détail",
        recommencer: "Recommencer",

        manquesTitre: "Il manque encore quelque chose",
        manquesAide: "Complétez les champs signalés pour obtenir votre calcul.",
        verifierTitre: "Une valeur à vérifier",
        verifierAide: "Ces champs sont remplis, mais il y a un problème dans ce qu'ils contiennent.",
        erreurFormat: "Ce format n'est pas reconnu.",
        erreurRevenu: "Indiquez un montant en euros, par exemple 24000.",
        erreurJours: "Indiquez un nombre de jours, par exemple 112.",

        alerteArretTropCourt: "Vous prévoyez {jours} jours d'arrêt, et le minimum est de {minimum}. Ce n'est pas une réduction : sous huit semaines, aucune indemnité journalière n'est versée. Vous laisseriez {perdu} de côté. L'allocation de repos maternel, elle, vous reste acquise.",
        alerteAudelaDuPlafond: "Vous prévoyez {jours} jours, et le congé n'en indemnise que {plafond}. Vous pouvez vous arrêter plus longtemps, mais les jours au-delà ne sont pas payés.",
        alerteRevenuSousLePlancher: "Votre revenu annuel moyen est inférieur à {plancher}. L'allocation et les indemnités passent alors à un dixième de leur montant : c'est une règle du barème, pas une erreur de calcul.",

        suiteTitre: "Et maintenant",
        suite1: "Prévenez l'Assurance Maladie de votre date d'arrêt, et déclarez sur l'honneur que vous cessez toute activité : c'est cette déclaration qui déclenche les indemnités.",
        suite2: "Régularisez vos échéances Urssaf avant de déposer la demande. Le droit s'ouvre sur des cotisations effectivement payées, pas seulement dues.",
        suite3: "Votre déclaration de chiffre d'affaires reste due à chaque échéance, à zéro s'il le faut : l'entreprise ne se met pas en pause, seule l'activité s'arrête.",
        suite4: "La cotisation foncière des entreprises reste due pour l'année entière. La réduction pour mois restant à courir vise les cessations d'activité, pas les interruptions.",

        fondTitre: "Une chose que nous ne chiffrons pas",
        fond: "Le fractionnement du congé de paternité d'un indépendant : trois versions différentes circulent sur les sources officielles, et les règles du salarié y sont souvent recopiées à tort. Demandez les vôtres à l'Assurance Maladie avant de poser vos dates.",
        supplementaireTitre: "Et ensuite : jusqu'à deux mois de plus",
        supplementaire: "Depuis le 1er juillet 2026, chaque parent peut ajouter 1 ou 2 mois de congé supplémentaire de naissance, à condition de suspendre son activité. L'indemnité est de {premier} bruts par jour le premier mois, puis de {second} le second, soit {moisUn} puis {moisDeux} pour des mois de trente jours.",
        supplementaireReduit: "Depuis le 1er juillet 2026, chaque parent peut ajouter 1 ou 2 mois de congé supplémentaire de naissance, à condition de suspendre son activité. Sous le plancher de revenu, l'indemnité est de {premier} bruts par jour les deux mois, soit {moisUn} pour un mois de trente jours.",
        supplementaireQuand: "À prendre juste après votre congé ou plus tard, jusqu'au {limite}, avec six mois d'affiliation au premier jour de ce congé. La demande se dépose en ligne, au plus tard la veille. L'autre parent y a droit de son côté, selon ses propres règles.",
        supplementaireAvant: "Le congé supplémentaire de naissance vise les enfants nés ou adoptés depuis le 1er janvier 2026 : cette naissance n'y ouvre pas droit.",

        acreTitre: "Le piège si vous venez de créer",
        acre: "L'ACRE se consomme même sans chiffre d'affaires. Un parent qui crée son activité juste avant la naissance puis s'arrête aussitôt épuise son exonération à vide, et doit attendre trois ans pour y avoir droit de nouveau.",

        source: "Montants relevés le 8 septembre 2026 sur ameli.fr et service-public.gouv.fr, ceux du congé supplémentaire de naissance le 25 septembre 2026. Ils se recalculent depuis le plafond annuel de la sécurité sociale : 48 060 divisé par 12 pour l'allocation, par 730 pour l'indemnité journalière.",
    },
    en: {
        phases: ["Your entitlement", "Your leave", "Your income"],
        retour: "Back",
        prive: "Nothing leaves this tab: the calculation runs in your browser, and everything is erased when you close it.",

        parentTitre: "Who is taking the leave?",
        parentAide: "The two kinds of leave differ in length and in what they pay, and only one carries a flat-rate allowance.",
        mere: "The mother",
        mereDetail: "Maternity leave: an allowance and daily benefits",
        second: "The second parent",
        secondDetail: "Paternity and childcare leave: daily benefits only",

        affiliationTitre: "Will you have been registered for six months by the due date?",
        affiliationTitreSecond: "Will you have been registered for six months when the leave starts?",
        affiliationAide: "Without these six months, social security pays nothing. That means six months of self-employed activity, not the twelve months sick leave requires: the confusion is common and expensive.",
        oui: "Yes",
        ouiDetail: "The business will be more than six months old",
        non: "No",
        nonDetail: "The business will be newer than that",

        horsSujetTitre: "The benefits are not open to you",
        horsSujet: "Maternity and paternity benefits for a self-employed worker require six months of registration by the expected date of birth, or when the leave starts. Below that, neither allowance nor daily benefits. Nothing stops you from taking time off, but social security will pay nothing: that is a budget to plan for another way, and one more reason not to start a business just before a birth.",

        naissanceTitre: "The birth",
        naissanceAide: "This date sets the whole calendar: the start of the leave, the compulsory period, and the end.",
        champDateAccouchement: "Expected date of birth",
        champDateNaissance: "Date of birth",
        aideDateNaissance: "Or the expected date, if it has not happened yet.",
        champSituation: "Your situation",
        premier: "One child, with no other dependant or just one",
        premierDetail: "16 weeks, 6 before and 10 after",
        troisieme: "One child, with two dependants already",
        troisiemeDetail: "26 weeks, 8 before and 18 after",
        jumeaux: "Twins",
        jumeauxDetail: "34 weeks, 12 before and 22 after",
        triples: "Triplets or more",
        triplesDetail: "46 weeks, 24 before and 22 after",
        champNaissances: "How many children are being born?",
        simple: "Just one",
        multiple: "Several",
        naissancesNote: "A single birth opens {simple} days, multiple births {multiple}.",

        arretTitre: "How long do you plan to stop?",
        arretAideMere: "Below eight weeks off, you receive no daily benefit at all, not even part of it.",
        arretAideSecond: "The leave is taken within the six months following the birth, in one go or in several parts.",
        champJours: "Number of days off",
        aideJoursMere: "The full leave opens {total}. The minimum to receive anything is {minimum}, of which {apres} after the birth.",
        aideJoursSecond: "{plafond} days at most.",
        toutLeConge: "Take the full leave",
        champReport: "Move part of the antenatal leave to the postnatal period",
        aideReport: "Up to {max} days, on a doctor's prescription, to be requested no later than the day before the planned start. The total does not change, it shifts.",

        revenuTitre: "Your income over the last three years",
        revenuAide: "Below a certain threshold, both the allowance and the benefits drop to a tenth. Above it, they are flat-rate: they do not rise with income.",
        champRevenuConnu: "Your average annual earned income",
        revenuChiffre: "I know it",
        revenuSous: "It is low",
        revenuSousDetail: "Less than {plancher}",
        champRevenuMoyen: "Average annual income",
        aideRevenuMoyen: "The average of your last three calendar years of activity. In a micro-enterprise, that is turnover after the allowance, not gross turnover.",

        continuer: "Continue",
        voirResultat: "See what I am entitled to",
        resultatTitre: "What you will receive",
        totalTitre: "Total benefits",
        recapTitre: "The detail",
        recommencer: "Start again",

        manquesTitre: "Something is still missing",
        manquesAide: "Fill in the highlighted fields to get your calculation.",
        verifierTitre: "Something to check",
        verifierAide: "These fields are filled in, but there is a problem with what they contain.",
        erreurFormat: "This format is not recognised.",
        erreurRevenu: "Enter an amount in euros, for example 24000.",
        erreurJours: "Enter a number of days, for example 112.",

        alerteArretTropCourt: "You are planning {jours} days off, and the minimum is {minimum}. This is not a reduction: below eight weeks, no daily benefit is paid at all. You would leave {perdu} on the table. The maternity rest allowance, however, stays yours.",
        alerteAudelaDuPlafond: "You are planning {jours} days, and the leave only pays for {plafond}. You may stop for longer, but the extra days are not paid.",
        alerteRevenuSousLePlancher: "Your average annual income is below {plancher}. The allowance and the benefits then drop to a tenth of their amount: that is a rule of the scale, not a calculation error.",

        suiteTitre: "What happens next",
        suite1: "Tell the health insurance the date you stop, and declare on oath that you are ceasing all activity: that declaration is what triggers the benefits.",
        suite2: "Settle your URSSAF instalments before filing. The entitlement opens on contributions actually paid, not merely owed.",
        suite3: "Your turnover declaration is still due at every deadline, at zero if need be: the business does not pause, only the work stops.",
        suite4: "The business property tax stays due for the whole year. The reduction for the remaining months applies to businesses that close, not to those that pause.",

        fondTitre: "One thing we do not put a figure on",
        fond: "How a self-employed parent may split paternity leave: three different versions circulate on official sources, and the rules for employees are often copied across by mistake. Ask the health insurance for yours before setting your dates.",
        supplementaireTitre: "And then: up to two more months",
        supplementaire: "Since 1 July 2026, each parent can add 1 or 2 months of supplementary birth leave, provided they suspend their activity. The benefit is {premier} gross a day in the first month, then {second} in the second, that is {moisUn} then {moisDeux} for thirty-day months.",
        supplementaireReduit: "Since 1 July 2026, each parent can add 1 or 2 months of supplementary birth leave, provided they suspend their activity. Below the income floor, the benefit is {premier} gross a day for both months, that is {moisUn} for a thirty-day month.",
        supplementaireQuand: "To be taken right after your leave or later, until {limite}, with six months of membership on the first day of that leave. The application is made online, no later than the day before. The other parent is entitled to it too, under their own rules.",
        supplementaireAvant: "Supplementary birth leave covers children born or adopted since 1 January 2026: this birth does not open the right to it.",

        acreTitre: "The trap if you have just started",
        acre: "The ACRE relief is used up even with no turnover. A parent who starts a business just before the birth and then stops straight away burns the exemption on an empty period, and has to wait three years to qualify again.",

        source: "Amounts recorded on 8 September 2026 from ameli.fr and service-public.gouv.fr, those of the supplementary birth leave on 25 September 2026. They recompute from the annual social security ceiling: 48,060 divided by 12 for the allowance, by 730 for the daily benefit.",
    },
};

const LIBELLES_MANQUES = {
    fr: {
        parent: "Qui prend le congé",
        dateAccouchement: "La date prévue de l'accouchement",
        dateNaissance: "La date de naissance",
        situation: "Votre situation familiale",
        naissances: "Le nombre d'enfants qui naissent",
        joursArret: "Le nombre de jours d'arrêt",
        revenuConnu: "Votre revenu annuel moyen",
        revenuMoyen: "Le montant de votre revenu annuel moyen",
    },
    en: {
        parent: "Who is taking the leave",
        dateAccouchement: "The expected date of birth",
        dateNaissance: "The date of birth",
        situation: "Your family situation",
        naissances: "How many children are being born",
        joursArret: "The number of days off",
        revenuConnu: "Your average annual income",
        revenuMoyen: "The amount of your average annual income",
    },
};

const PHASES_PAR_ECRAN = {
    parent: 0,
    affiliation: 0,
    horsSujet: 0,
    naissance: 1,
    arret: 1,
    revenu: 2,
    resultat: 2,
};

const VIDE = {
    parent: "",
    affiliation: "",
    dateAccouchement: "",
    dateNaissance: "",
    situation: "",
    naissances: "",
    joursArret: "",
    reportPrenatal: "",
    revenuConnu: "",
    revenuMoyen: "",
};

/** Les quatre situations de la maternité, dans l'ordre du barème. */
const SITUATIONS = ["premier", "troisieme", "jumeaux", "triples"];

export default function CongeParental({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const [r, setR] = useState(VIDE);

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "parent",
        () => setR(VIDE),
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));
    const mere = r.parent === "mere";

    const controle = useMemo(() => valider(r), [r]);
    const droits = useMemo(() => (mere ? droitsMaternite(r) : droitsPaternite(r)), [r, mere]);
    const recap = useMemo(() => recapitulatif(r), [r]);
    // Le total dit d'où il vient : l'allocation, les indemnités, et leurs sources.
    const ex = useMemo(() => expliquerCongeNaissance(droits, lang), [droits, lang]);
    // Le congé supplémentaire de naissance, qui s'ouvre après celui-ci.
    const supplementaire = useMemo(() => congeSupplementaire(r), [r]);
    const exSupplementaire = useMemo(() => expliquerCongeSupplementaire(supplementaire, lang), [supplementaire, lang]);
    const { ouverte, basculer } = useExplications();
    const tri = concerne(r);

    const erreurDe = (champ) => {
        const refus = (controle.invalides || []).find((i) => i.champ === champ);
        if (!refus) return "";
        if (champ === "revenuMoyen") return t.erreurRevenu;
        if (champ === "joursArret") return t.erreurJours;
        return t.erreurFormat;
    };

    const rempli = (...cles) => cles.every((c) => String(r[c] || "").trim());
    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };
    const plafondPaternite =
        r.naissances === "multiple" ? PATERNITE_JOURS.multiple : PATERNITE_JOURS.simple;
    const totalDuConge = mere ? MATERNITE[r.situation]?.total : plafondPaternite;

    /* ---------------------------------------------------------------- */
    /* Le tri                                                            */
    /* ---------------------------------------------------------------- */

    if (etape === "parent") {
        return (
            <Ecran key={etape} {...commun} phase={0} titre={t.parentTitre} aide={t.parentAide}>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Choix
                        icone="user-heart-rounded-linear"
                        titre={t.mere}
                        detail={t.mereDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, parent: "mere" }));
                            aller("affiliation");
                        }}
                    />
                    <Choix
                        icone="user-rounded-linear"
                        titre={t.second}
                        detail={t.secondDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, parent: "second" }));
                            aller("affiliation");
                        }}
                    />
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "affiliation") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={0}
                onRetour={retour}
                titre={mere ? t.affiliationTitre : t.affiliationTitreSecond}
                aide={t.affiliationAide}
            >
                <div className="grid sm:grid-cols-2 gap-3">
                    <Choix
                        icone="check-circle-linear"
                        titre={t.oui}
                        detail={t.ouiDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, affiliation: "oui" }));
                            aller("naissance");
                        }}
                    />
                    <Choix
                        icone="close-circle-linear"
                        titre={t.non}
                        detail={t.nonDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, affiliation: "non" }));
                            aller("horsSujet");
                        }}
                    />
                </div>
            </Ecran>
        );
    }

    if (etape === "horsSujet") {
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.horsSujetTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4">
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-0">
                        {remplir(t.horsSujet, { mois: AFFILIATION_MOIS })}
                    </p>
                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            </Resultat>
        );
    }

    if (etape === "naissance") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.naissanceTitre} aide={t.naissanceAide}>
                <div className="space-y-5">
                    <Champ
                        type="date"
                        libelle={mere ? t.champDateAccouchement : t.champDateNaissance}
                        aide={mere ? "" : t.aideDateNaissance}
                        valeur={mere ? r.dateAccouchement : r.dateNaissance}
                        poser={poser(mere ? "dateAccouchement" : "dateNaissance")}
                        erreur={erreurDe(mere ? "dateAccouchement" : "dateNaissance")}
                    />
                    {mere ? (
                        <div>
                            <span className="block text-xs text-gray-500 dark:text-lightBlue/70 mb-2.5">
                                {t.champSituation}
                            </span>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {SITUATIONS.map((cle) => (
                                    <Choix
                                        key={cle}
                                        icone={r.situation === cle ? "check-circle-bold" : "check-circle-linear"}
                                        titre={t[cle]}
                                        detail={t[`${cle}Detail`]}
                                        onClick={() => setR((p) => ({ ...p, situation: cle }))}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div>
                            <Segments
                                libelle={t.champNaissances}
                                options={[
                                    { cle: "simple", libelle: t.simple },
                                    { cle: "multiple", libelle: t.multiple },
                                ]}
                                valeur={r.naissances}
                                poser={poser("naissances")}
                            />
                            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-2.5 mb-0">
                                {remplir(t.naissancesNote, {
                                    simple: PATERNITE_JOURS.simple,
                                    multiple: PATERNITE_JOURS.multiple,
                                })}
                            </p>
                        </div>
                    )}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli(mere ? "dateAccouchement" : "dateNaissance") ||
                        !rempli(mere ? "situation" : "naissances")
                    }
                    onClick={() => aller("arret")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "arret") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={1}
                onRetour={retour}
                titre={t.arretTitre}
                aide={mere ? t.arretAideMere : t.arretAideSecond}
            >
                <div className="space-y-5">
                    <Champ
                        libelle={t.champJours}
                        aide={
                            mere
                                ? remplir(t.aideJoursMere, {
                                      total: totalDuConge,
                                      minimum: ARRET_MINIMAL_JOURS,
                                      apres: ARRET_MINIMAL_APRES_JOURS,
                                  })
                                : remplir(t.aideJoursSecond, { plafond: plafondPaternite })
                        }
                        valeur={r.joursArret}
                        poser={poser("joursArret")}
                        erreur={erreurDe("joursArret")}
                        inputMode="numeric"
                    />
                    <button
                        type="button"
                        onClick={() => setR((p) => ({ ...p, joursArret: String(totalDuConge) }))}
                        className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-lightBlue hover:text-primary transition-colors duration-200"
                    >
                        <SolarIcon name="calendar-linear" className="text-lg" />
                        {t.toutLeConge}
                    </button>
                    {mere && (
                        <Champ
                            libelle={t.champReport}
                            aide={remplir(t.aideReport, { max: REPORT_PRENATAL_MAX_JOURS })}
                            valeur={r.reportPrenatal}
                            poser={poser("reportPrenatal")}
                            inputMode="numeric"
                        />
                    )}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("joursArret") || Boolean(erreurDe("joursArret"))}
                    onClick={() => aller("revenu")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "revenu") {
        return (
            <Ecran key={etape} {...commun} phase={2} onRetour={retour} titre={t.revenuTitre} aide={t.revenuAide}>
                <div className="space-y-5">
                    <div>
                        <Segments
                            libelle={t.champRevenuConnu}
                            options={[
                                { cle: "chiffre", libelle: t.revenuChiffre },
                                {
                                    cle: "sous",
                                    libelle: `${t.revenuSous} (${remplir(t.revenuSousDetail, {
                                        plancher: euros(REVENU_PLANCHER),
                                    })})`,
                                },
                            ]}
                            valeur={r.revenuConnu}
                            poser={poser("revenuConnu")}
                        />
                    </div>
                    {r.revenuConnu === "chiffre" && (
                        <Champ
                            libelle={t.champRevenuMoyen}
                            aide={t.aideRevenuMoyen}
                            valeur={r.revenuMoyen}
                            poser={poser("revenuMoyen")}
                            erreur={erreurDe("revenuMoyen")}
                            inputMode="decimal"
                        />
                    )}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !r.revenuConnu ||
                        (r.revenuConnu === "chiffre" &&
                            (!rempli("revenuMoyen") || Boolean(erreurDe("revenuMoyen"))))
                    }
                    onClick={() => aller("resultat")}
                >
                    {t.voirResultat}
                </Avancer>
            </Ecran>
        );
    }

    /* ---------------------------------------------------------------- */
    /* Le résultat                                                       */
    /* ---------------------------------------------------------------- */

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {!recap || !droits ? (
                <div className="mt-4">
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {controle.manques.length ? t.manquesTitre : t.verifierTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {controle.manques.length ? t.manquesAide : t.verifierAide}
                    </p>
                    <ul className="mt-4 space-y-2">
                        {controle.manques.map((cle) => (
                            <li key={cle} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{LIBELLES_MANQUES[lang]?.[cle] || LIBELLES_MANQUES.fr[cle]}</span>
                            </li>
                        ))}
                        {(controle.invalides || []).map(({ champ, motif }) => (
                            <li
                                key={`${champ}-${motif}`}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                <span>
                                    {LIBELLES_MANQUES[lang]?.[champ] || LIBELLES_MANQUES.fr[champ]}
                                    {" : "}
                                    {erreurDe(champ)}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 p-5 mb-6">
                        <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-2">
                            {t.totalTitre}
                        </div>
                        <div className="text-3xl font-bold dark:text-white">
                            <MontantExplicable explication={ex.total} ouvert={ouverte === "total"} onBasculer={() => basculer("total")}>
                                {euros(droits.total)}
                            </MontantExplicable>
                        </div>
                        {ouverte === "total" ? (
                            <PanneauExplication explication={ex.total} lang={lang} />
                        ) : (
                            <IndiceExplication lang={lang} />
                        )}
                    </div>

                    {controle.alertes.includes("arretTropCourt") && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {remplir(t.alerteArretTropCourt, {
                                    jours: droits.joursArret,
                                    minimum: ARRET_MINIMAL_JOURS,
                                    perdu: euros(droits.perdu),
                                })}
                            </p>
                        </div>
                    )}
                    {controle.alertes.includes("audelaDuPlafond") && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {remplir(t.alerteAudelaDuPlafond, {
                                    jours: droits.joursArret,
                                    plafond: droits.calendrier.plafond,
                                })}
                            </p>
                        </div>
                    )}
                    {controle.alertes.includes("revenuSousLePlancher") && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {remplir(t.alerteRevenuSousLePlancher, { plancher: euros(REVENU_PLANCHER) })}
                            </p>
                        </div>
                    )}

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.recapTitre}
                    </div>
                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden mb-6">
                        <table className="w-full text-sm">
                            <tbody>
                                {recap.map(([titre, valeur]) => (
                                    <tr
                                        key={titre}
                                        className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10"
                                    >
                                        <td className="px-5 py-3 text-gray-500 dark:text-lightBlue/70 align-top w-2/5">
                                            {titre}
                                        </td>
                                        <td className="px-5 py-3 font-medium dark:text-white">{valeur}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon
                                name="clipboard-check-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">{t.suiteTitre}</div>
                        </div>
                        <Liste items={[t.suite1, t.suite2, t.suite3, t.suite4]} />
                    </div>

                    {/* LE CONGÉ SUPPLÉMENTAIRE DE NAISSANCE, ouvert depuis le
                        1er juillet 2026 : un ou deux mois de plus, pour chaque
                        parent, que ni le total ni le détail ne comptent. */}
                    {supplementaire && (
                        <div className="mt-8 rounded-2xl border border-primary/30 bg-primary/[0.04] p-5">
                            <div className="flex items-center gap-2.5 mb-3">
                                <SolarIcon
                                    name="calendar-add-linear"
                                    className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                                />
                                <div className="font-semibold dark:text-white leading-snug">{t.supplementaireTitre}</div>
                            </div>
                            {supplementaire.ouvert ? (
                                <>
                                    <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                                        {phraseAvecMontants(supplementaire.reduit ? t.supplementaireReduit : t.supplementaire, {
                                            premier: (
                                                <MontantExplicable
                                                    explication={exSupplementaire.premier}
                                                    ouvert={ouverte === "supplementairePremier"}
                                                    onBasculer={() => basculer("supplementairePremier")}
                                                >
                                                    {euros(supplementaire.parJour[0])}
                                                </MontantExplicable>
                                            ),
                                            second: (
                                                <MontantExplicable
                                                    explication={exSupplementaire.second}
                                                    ouvert={ouverte === "supplementaireSecond"}
                                                    onBasculer={() => basculer("supplementaireSecond")}
                                                >
                                                    {euros(supplementaire.parJour[1])}
                                                </MontantExplicable>
                                            ),
                                            moisUn: euros(supplementaire.moisDeTrenteJours[0]),
                                            moisDeux: euros(supplementaire.moisDeTrenteJours[1]),
                                        })}
                                    </p>
                                    {ouverte === "supplementairePremier" && (
                                        <PanneauExplication explication={exSupplementaire.premier} lang={lang} />
                                    )}
                                    {ouverte === "supplementaireSecond" && (
                                        <PanneauExplication explication={exSupplementaire.second} lang={lang} />
                                    )}
                                    <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mt-3 mb-0">
                                        {remplir(t.supplementaireQuand, { limite: dateLisible(supplementaire.limite, lang) })}
                                    </p>
                                </>
                            ) : (
                                <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">{t.supplementaireAvant}</p>
                            )}
                        </div>
                    )}

                    <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon
                                name="rocket-2-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-semibold dark:text-white leading-snug">{t.acreTitre}</div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">{t.acre}</p>
                    </div>

                    <div className="mt-5 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon
                                name="info-circle-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-semibold dark:text-white leading-snug">{t.fondTitre}</div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">{t.fond}</p>
                    </div>

                    <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">
                        {remplir(t.source, { allocation: euros(ALLOCATION_REPOS), ij: euros(IJ_FORFAITAIRE) })}
                    </p>

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
